"use client";

import RevealOnScroll from "@/components/atoms/RevealOnScroll";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import type { FC } from "react";
import { useEffect, useMemo, useRef } from "react";

import {
  LFL_DEFAULT_DESCRIPTION,
  LFL_DEFAULT_HEADING_LINE_1,
  LFL_DEFAULT_HEADING_LINE_2,
} from "./learnFromLeadersCopy";

type LflProfile = {
  id?: number | string;
  desktop_image?: string;
  mobile_image?: string;
  image_alt?: string;
  profile_name?: string;
  profile_title?: string;
  profile_name_color?: string;
  profile_title_color?: string;
  card_overlay_gradient_start?: string;
  card_overlay_gradient_end?: string;
  card_overlay_gradient_angle?: string;
  show_card_overlay_gradient?: boolean | number | string;
  show_divider?: boolean | number | string;
  logo_1?: string;
  logo_1_alt?: string;
  logo_2?: string;
  logo_2_alt?: string;
  logos_layout?: string;
  is_active?: boolean | number;
};

function toBool(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["0", "false", "no", "off", ""].includes(t)) return false;
    if (["1", "true", "yes", "on"].includes(t)) return true;
  }
  return Boolean(value);
}

function pickImageUrl(p: LflProfile, useMobileImage: boolean) {
  if (useMobileImage && p.mobile_image?.trim()) return p.mobile_image.trim();
  return (p.desktop_image || p.mobile_image || "").trim();
}

const LflLeaderCard: FC<{
  profile: LflProfile;
  imageUrl: string;
}> = ({ profile, imageUrl }) => {
  const showGradient = toBool(profile.show_card_overlay_gradient, true);
  const showDivider = toBool(profile.show_divider, true);
  const profileName = profile.profile_name?.trim() ?? "";

  return (
    <div
      className={cn(
        "relative h-[350px] w-[240px] shrink-0 overflow-hidden rounded-[20px]",
        "bg-neutral-900 shadow-lg shadow-black/40",
        "transition-transform duration-300 ease-out",
        "hover:scale-85 hover:-translate-y-4 hover:-rotate-3",
      )}
    >
      {imageUrl ? (
        <Image
          src={imageUrl}
          alt={profile.image_alt || profile.profile_name || ""}
          fill
          className="object-cover object-center"
          decoding="async"
          sizes="240px"
        />
      ) : (
        <div className="h-full w-full bg-neutral-800" />
      )}

      {showGradient && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-[126px]"
          style={{
            backgroundImage: `linear-gradient(179.875deg, rgba(0, 0, 0, 0) 0.20752%, rgba(0, 0, 0, 0.9) 61.263%)`,
          }}
          aria-hidden
        />
      )}

      <div className="absolute bottom-0 left-1/2 z-30 flex w-[240px] -translate-x-1/2 flex-col items-center gap-[10px] pb-5">
        {profileName && (
          <p
            className="truncate text-center text-[16px] font-semibold leading-[1.4] tracking-[0.16px] text-white"
            style={{
              fontFamily: "var(--font-google-sans)",
              color: profile.profile_name_color || "#FFFFFF",
            }}
          >
            {profileName}
          </p>
        )}
        {showDivider && (
          <div className="h-px w-[35px] shrink-0 rounded-full bg-white/80" aria-hidden />
        )}
        <div className="flex flex-col items-center gap-[5px]">
          <p
            className="text-center text-[12px] font-medium italic leading-[1.4] tracking-[0.12px] text-white/80"
            style={{
              fontFamily: "var(--font-google-sans)",
              color: profile.profile_title_color || "rgba(255,255,255,0.8)",
            }}
          >
            {profile.profile_title || ""}
          </p>
          {profile.logo_1 && (
            <div className="relative h-[16px] w-[121px]">
              <Image
                src={profile.logo_1}
                alt={profile.logo_1_alt || ""}
                fill
                className="object-contain"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SCROLL_SPEED = 0.1;        // px per ms — normal
const HOVER_SCROLL_SPEED = 0.05; // px per ms — on card hover
const DRAG_MULTIPLIER = 3;
const LEAVE_MULTIPLIER = 1.1;
const CARD_STEP = 260;            // 240px card + 20px gap
const FRICTION = 0.95;            // velocity multiplier per 16ms frame
const MOMENTUM_THRESHOLD = 0.05;  // px/ms — below this, resume auto-scroll

const InfiniteCardsStrip: FC<{
  profiles: LflProfile[];
  useMobileImage: boolean;
  edgeFadeColor?: string;
}> = ({ profiles, useMobileImage, edgeFadeColor = "#000000" }) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0);
  const rafRef = useRef<number>(0);
  const isDragging = useRef(false);
  const isHovered = useRef(false);
  const dragStartX = useRef(0);
  const dragStartPos = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);     // px/ms — positive = moving right
  const lastPointerX = useRef(0);
  const lastPointerTime = useRef(0);

  // Width of one full copy of the strip — used as the loop boundary
  const loopWidth = profiles.length * CARD_STEP;

  // Three copies so there's always content left/right of the visible window
  const tripled = useMemo(
    () => [...profiles, ...profiles, ...profiles],
    [profiles],
  );

  useEffect(() => {
    if (!profiles.length) return;

    const tick = (time: number) => {
      const dt = lastTimeRef.current ? time - lastTimeRef.current : 0;
      lastTimeRef.current = time;

      if (!isDragging.current) {
        if (Math.abs(velocityRef.current) > MOMENTUM_THRESHOLD) {
          posRef.current += velocityRef.current * dt;
          velocityRef.current *= Math.pow(FRICTION, dt / 16);
        } else {
          velocityRef.current = 0;
          const speed = isHovered.current ? HOVER_SCROLL_SPEED : SCROLL_SPEED;
          posRef.current -= speed * dt;
        }
      }

      // Keep position within (-loopWidth, 0] for seamless looping
      if (posRef.current <= -loopWidth) posRef.current += loopWidth;
      if (posRef.current > 0) posRef.current -= loopWidth;

      if (stripRef.current) {
        stripRef.current.style.transform = `translateX(${posRef.current}px)`;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lastTimeRef.current = 0;
    };
  }, [loopWidth, profiles.length]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDragging.current = true;
    velocityRef.current = 0;
    dragStartX.current = e.clientX;
    dragStartPos.current = posRef.current;
    lastPointerX.current = e.clientX;
    lastPointerTime.current = performance.now();
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;

    // Track instantaneous velocity (with multiplier) for momentum on release
    const now = performance.now();
    const elapsed = now - lastPointerTime.current;
    if (elapsed > 0) {
      velocityRef.current = ((e.clientX - lastPointerX.current) / elapsed) * LEAVE_MULTIPLIER;
    }
    lastPointerX.current = e.clientX;
    lastPointerTime.current = now;

    const rawDelta = (e.clientX - dragStartX.current) * DRAG_MULTIPLIER;
    let newPos = (((dragStartPos.current + rawDelta) % loopWidth) + loopWidth) % loopWidth;
    if (newPos > 0) newPos -= loopWidth;
    posRef.current = newPos;
    if (stripRef.current) {
      stripRef.current.style.transform = `translateX(${newPos}px)`;
    }
  };

  const onPointerUp = () => {
    isDragging.current = false;
  };

  return (
    <div
      className="relative h-[350px] w-full overflow-hidden cursor-ew-resize select-none"
      style={{ touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onMouseEnter={() => { isHovered.current = true; }}
      onMouseLeave={() => { isHovered.current = false; }}
    >
      {/* Edge fades */}
      <div
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          backgroundImage: `linear-gradient(270deg, ${edgeFadeColor} 0%, rgba(0,0,0,0) 15%, rgba(0,0,0,0) 85%, ${edgeFadeColor} 100%)`,
        }}
        aria-hidden
      />

      <div
        ref={stripRef}
        className="absolute top-0 left-0 flex h-full items-center gap-[20px] will-change-transform"
      >
        {tripled.map((profile, idx) => (
          <LflLeaderCard
            key={`lfl-${idx}`}
            profile={profile}
            imageUrl={pickImageUrl(profile, useMobileImage)}
          />
        ))}
      </div>
    </div>
  );
};

const LearnFromLeaders: FC<{ data: any }> = ({ data }) => {
  const actualData = data?.data ?? data;
  const ref = useRef<HTMLElement>(null);
  const useMobileImage = useIsMobile(768);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "learn_from_leaders_section");
  //   }
  // }, []);

  const rawProfiles = (actualData as { profiles?: unknown } | null)?.profiles;
  const rawMetadata = (actualData as { metadata?: unknown } | null)?.metadata;
  const profiles: LflProfile[] =
    Array.isArray(rawProfiles) && rawProfiles.length > 0
      ? (rawProfiles as LflProfile[])
      : Array.isArray(rawMetadata)
        ? (rawMetadata as LflProfile[])
        : [];

  const activeProfiles = useMemo(() => {
    return profiles.filter(
      (p) =>
        p.is_active !== false &&
        p.is_active !== 0 &&
        pickImageUrl(p, useMobileImage === true),
    );
  }, [profiles, useMobileImage]);

  // Early returns after all hooks
  if (actualData == null || typeof actualData !== "object") return null;
  if (actualData.status === false || actualData.status === 0) return null;
  if (useMobileImage === null) return null;

  const {
    pre_title,
    title,
    pre_title_color,
    title_start_color,
    title_end_color,
    enable_animation,
  } = actualData as Record<string, unknown>;

  const desktopBgRaw = (actualData as Record<string, unknown>).desktop_background_color;
  const mobileBgRaw = (actualData as Record<string, unknown>).mobile_background_color;
  const desktopBg =
    typeof desktopBgRaw === "string" && desktopBgRaw.trim() ? desktopBgRaw.trim() : "#000000";
  const mobileBg =
    typeof mobileBgRaw === "string" && mobileBgRaw.trim() ? mobileBgRaw.trim() : "#000000";

  const rawDescription = (actualData as { description?: unknown }).description;
  const description =
    typeof rawDescription === "string" && rawDescription.trim().length > 0
      ? rawDescription.trim()
      : LFL_DEFAULT_DESCRIPTION;

  const rawDescriptionColor = (actualData as { description_color?: unknown }).description_color;
  const descriptionColor =
    typeof rawDescriptionColor === "string" && rawDescriptionColor.trim().length > 0
      ? rawDescriptionColor.trim()
      : "rgba(255, 255, 255, 0.7)";

  const headingLine1 =
    typeof pre_title === "string" && pre_title.trim().length > 0
      ? pre_title.trim()
      : LFL_DEFAULT_HEADING_LINE_1;
  const headingLine2 =
    typeof title === "string" && title.trim().length > 0
      ? title.trim()
      : LFL_DEFAULT_HEADING_LINE_2;

  const line1Color =
    (typeof pre_title_color === "string" && pre_title_color.trim()) ||
    (typeof title_start_color === "string" && title_start_color.trim()) ||
    "#FFFFFF";
  const line2Color =
    (typeof title_end_color === "string" && title_end_color.trim()) || "#02A7B6";

  const isAnimationEnabled =
    enable_animation === true || enable_animation === 1 || enable_animation === "1";

  const backgroundColor = useMobileImage === true ? mobileBg : desktopBg;

  return (
    <section
      ref={ref}
      aria-label="Learn from leaders"
      className={cn(
        "w-full text-white",
        "py-[40px] md:py-[60px] xl:py-[80px]",
        "px-[20px] sm:px-[60px] md:px-[80px] lg:px-[100px] xl:px-[160px]",
      )}
      style={{ backgroundColor }}
      data-widget="learn-from-leaders"
    >
      <div className="mx-auto flex w-full max-w-[1750px] flex-col gap-[40px] sm:flex-row sm:items-center xl:gap-[80px]">
        {/* Left column — text */}
        <div
          className={cn(
            "flex w-full flex-col gap-[24px]",
            "sm:shrink-0",
            "sm:w-[340px] md:w-[380px] lg:w-[430px] xl:w-[520px]",
          )}
        >
          <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <h2 className="m-0 flex flex-col gap-[4px] p-0">
              <span
                className="block text-[20px] sm:text-[22px] md:text-[26px] lg:text-[28px]"
                style={{
                  fontFamily: "var(--font-google-sans)",
                  fontWeight: 600,
                  lineHeight: "1.4",
                  color: line1Color,
                }}
              >
                {headingLine1}
              </span>
              <span
                className="block text-[20px] sm:text-[22px] md:text-[26px] lg:text-[28px]"
                style={{
                  fontFamily: "var(--font-google-sans)",
                  fontWeight: 600,
                  lineHeight: "1.4",
                  color: line2Color,
                }}
              >
                {headingLine2}
              </span>
            </h2>
          </RevealOnScroll>

          <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <p
              className="m-0 p-0 text-[14px] md:text-[16px] lg:text-[18px]"
              style={{
                fontFamily: "var(--font-google-sans)",
                fontWeight: 600,
                lineHeight: "1.49",
                color: descriptionColor,
              }}
            >
              {description}
            </p>
          </RevealOnScroll>
        </div>

        {/* Right column — infinite scroll strip */}
        <div className="min-w-0 flex-1">
          {activeProfiles.length > 0 && (
            <InfiniteCardsStrip
              profiles={activeProfiles}
              useMobileImage={useMobileImage === true}
              edgeFadeColor={backgroundColor}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default LearnFromLeaders;
